import {useState} from 'react'
import {useMutation, useQueryClient} from '@tanstack/react-query'
import {DragEndEvent} from '@dnd-kit/core'
import {arrayMove} from '@dnd-kit/sortable'
import {useServices} from '@/services/context'
import type {ValueListEntry} from '@/types/value-list'

export interface UseEntryReorderResult {
    /** The locally-overridden order if a drag is in flight; `null` otherwise. */
    orderedEntries: ValueListEntry[] | null
    /** Convenience: the order to actually render — local override OR the server order. */
    entries: ValueListEntry[]
    /** dnd-kit `onDragEnd` handler. */
    handleDragEnd: (event: DragEndEvent) => void
    /** Resets local order — the page calls this after a non-reorder mutation succeeds. */
    resetLocalOrder: () => void
    /** True while the server-side reorder PUT is in flight. */
    isReordering: boolean
}

/**
 * DnD-aware reorder hook.
 *
 * WHY: optimistic local update — we replace the rendered order immediately
 * on drag-end, then fire the server PUT in the background. On error we
 * invalidate the parent query so the server's order wins, restoring the
 * pre-drag list.
 */
export function useEntryReorder(
    listId: string | undefined,
    serverEntries: ValueListEntry[],
): UseEntryReorderResult {
    const services = useServices()
    const queryClient = useQueryClient()
    const [orderedEntries, setOrderedEntries] = useState<ValueListEntry[] | null>(null)

    const reorderMutation = useMutation({
        mutationFn: (entryIds: string[]) =>
            services.valueLists.reorderEntries(listId!, {entryIds}),
        onError: () => {
            queryClient.invalidateQueries({queryKey: ['value-list', listId]})
            setOrderedEntries(null)
        },
    })

    const entries = orderedEntries ?? serverEntries

    function handleDragEnd(event: DragEndEvent) {
        const {active, over} = event
        if (!over || active.id === over.id) return

        const current = orderedEntries ?? serverEntries
        const oldIndex = current.findIndex(e => e.id === active.id)
        const newIndex = current.findIndex(e => e.id === over.id)
        if (oldIndex === -1 || newIndex === -1) return

        const reordered = arrayMove(current, oldIndex, newIndex)
        setOrderedEntries(reordered)
        reorderMutation.mutate(reordered.map(e => e.id))
    }

    function resetLocalOrder() {
        setOrderedEntries(null)
    }

    return {
        orderedEntries,
        entries,
        handleDragEnd,
        resetLocalOrder,
        isReordering: reorderMutation.isPending,
    }
}
